import { type MaintenanceChecklistField } from '@gb-ticket/shared-types';

import { acroformFieldBoxStyle } from '@/products/maintenance/features/protocol/protocol-acroform-layout';

type Props = {
  fields: MaintenanceChecklistField[];
  values: Record<string, unknown>;
  disabled?: boolean;
  onValueChange: (fieldKey: string, value: unknown) => void;
};

function textValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  return '';
}

export function ProtocolAcroformFields({ fields, values, disabled, onValueChange }: Props) {
  return (
    <>
      {fields.map((field) => {
        if (!field.placement || field.type === 'info' || field.type === 'attachment') {
          return null;
        }
        const style = acroformFieldBoxStyle(field.placement);
        const value = values[field.fieldKey];
        const label = field.required ? `${field.label} *` : field.label;
        const boxClass =
          'absolute z-10 box-border min-h-0 min-w-0 overflow-hidden rounded-sm border border-primary/70 bg-background/95 text-sm leading-tight shadow-sm touch-manipulation';

        if (field.type === 'boolean') {
          return (
            <label
              key={field.fieldKey}
              className={`${boxClass} flex items-center justify-center p-0.5`}
              style={style}
            >
              <input
                type="checkbox"
                aria-label={label}
                className="size-full max-h-full max-w-full"
                checked={value === true}
                disabled={disabled}
                onChange={(event) => onValueChange(field.fieldKey, event.target.checked)}
              />
            </label>
          );
        }

        if (field.type === 'choice') {
          return (
            <select
              key={field.fieldKey}
              aria-label={label}
              className={`${boxClass} h-full w-full px-0.5`}
              style={style}
              value={textValue(value)}
              disabled={disabled}
              onChange={(event) => onValueChange(field.fieldKey, event.target.value)}
            >
              <option value="">{field.label}</option>
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );
        }

        if (field.type === 'long_text') {
          return (
            <textarea
              key={field.fieldKey}
              aria-label={label}
              className={`${boxClass} h-full w-full resize-none px-1 py-0.5`}
              style={style}
              value={textValue(value)}
              disabled={disabled}
              onChange={(event) => onValueChange(field.fieldKey, event.target.value)}
            />
          );
        }

        const numeric =
          field.type === 'integer' || field.type === 'decimal' || field.type === 'measurement';
        return (
          <input
            key={field.fieldKey}
            aria-label={label}
            className={`${boxClass} h-full w-full px-1`}
            style={style}
            type={
              field.type === 'date'
                ? 'date'
                : field.type === 'time'
                  ? 'time'
                  : numeric
                    ? 'number'
                    : 'text'
            }
            value={textValue(value)}
            disabled={disabled}
            onChange={(event) => {
              const next = event.target.value;
              if (
                field.type === 'integer' ||
                field.type === 'decimal' ||
                field.type === 'measurement'
              ) {
                onValueChange(field.fieldKey, next === '' ? null : Number(next));
                return;
              }
              onValueChange(field.fieldKey, next);
            }}
          />
        );
      })}
    </>
  );
}
